import React from 'react';

import { i18n } from 'config/locales';

import { Summary, AddressSummary, DateSummary } from 'components/Summary';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection from 'components/Section/shared/Subsection';
import { FINANCIAL, FINANCIAL_BANKRUPTCY } from 'config/formSections/financial';

import Field from 'components/Form/Field';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import ErrorMessage from 'components/ErrorMessage';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { financialRecordAddressNameFields } from 'constants/enums/people';
import { isPVQForm } from 'helpers/form';
import { EmptyListInstance } from 'forms/shared/models';
import Bankruptcy from './Bankruptcy';

const sectionConfig = {
  key: FINANCIAL_BANKRUPTCY.key,
  section: FINANCIAL.name,
  store: FINANCIAL.store,
  subsection: FINANCIAL_BANKRUPTCY.name,
  storeKey: FINANCIAL_BANKRUPTCY.storeKey,
};

export class Bankruptcies extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      HasBankruptcy: this.props.HasBankruptcy,
      ...queue,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateHasBankruptcy = (values) => {
    this.update({
      HasBankruptcy: values,
      List: values?.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  };

  /**
   * Assists in rendering the summary section.
   */
  summary = (item, index) => {
    const b = item.Item || {};
    const from = DateSummary(b.DateFiled);
    const address = AddressSummary(b.CourtAddress);

    return Summary({
      type: i18n.t('financial.bankruptcy.collection.summary.item'),
      index,
      left: address,
      right: from,
      placeholder: i18n.t('financial.bankruptcy.collection.summary.unknown'),
    });
  };

  render() {
    const {
      errors, requireFinancialBankruptcyTrusteeInformation, peopleWhoKnowYou, List
    } = this.props;

    const accordionErrors = errors && errors.filter((e) => e?.indexOf?.('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, financialRecordAddressNameFields);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPVQ = isPVQForm(this.props.formType);

    return (
      <div
        className="section-content bankruptcies"
        data-section={FINANCIAL.key}
        data-subsection={FINANCIAL_BANKRUPTCY.key}
      >
        <h1 className="section-header">{i18n.t('financial.destination.bankruptcy')}</h1>
        <Branch
          name="has_bankruptcydebt"
          label={isPVQ ? i18n.t('financial.pvq.bankruptcy.branch.title') : i18n.t('financial.bankruptcy.title')}
          labelSize="h4"
          className="bankruptcy-branch"
          {...this.props.HasBankruptcy}
          help="financial.bankruptcy.help"
          warning
          onUpdate={this.updateHasBankruptcy}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          attribute="HasBankruptcy"
        />
        <Show when={(this.props.HasBankruptcy || {}).value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            errors={accordionErrors}
            scrollIntoView={this.props.scrollIntoView}
            summary={this.summary}
            description={i18n.t('financial.bankruptcy.collection.summary.title')}
            appendTitle={isPVQ ? i18n.t('financial.pvq.bankruptcy.branch.appendTitle') : i18n.t('financial.bankruptcy.collection.summary.appendTitle')}
            appendLabel={i18n.t('financial.bankruptcy.collection.append')}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <Bankruptcy
              name="Item"
              requireFinancialBankruptcyTrusteeInformation={
                requireFinancialBankruptcyTrusteeInformation
              }
              formType={this.props.formType}
              dispatch={this.props.dispatch}
              addressBooks={this.props.addressBooks}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              bind
              attribute="List"
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Bankruptcies.defaultProps = {
  HasBankruptcy: {},
  List: EmptyListInstance,
  addressBooks: {},
  onUpdate: () => {},
  onError: (value, arr) => arr,
  dispatch: () => {},
  defaultState: true,
  scrollToBottom: '.bottom-btns',
  errors: [],
};

export default connectSubsection(Bankruptcies, sectionConfig);
